'use strict';

import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import classNames from 'classnames';
import platform from 'platform';

import SpringHero from '@components/MealDetails/SpringHero.react';
import MealNutritionTabs from './MealNutritionTabs.react';
import RecipeContentDisplay from './RecipeContentDisplay.react';
import FoodContentDisplay from './FoodContentDisplay.react';
import ControlBar from '../../actions/ControlBar.react';
import EditMealServingsButton from '../../wrappers/EditMealServingsButton.react';
import HowMuchToEatSection from './HowMuchToEatSection.react';

import { useAssets, useMealPlanData, useMealOperations, usePermissions, useUIState, useModalControls } from '../../contexts';
import { getPrimaryMeal, isScalable, mealSortCompare, enrichMealsForSorting } from '@utils/Meals';
import { toHuman } from '@utils/Duration';
import { ScreenService } from '@utils/ScreenService';

import './MealDetails.scss';

const sections = ['first', 'second', 'third', 'fourth', 'fifth'];

const MealDetails = ({
    offset,
    mealType,
    closeModal,
    hidePrintBtn = false
}) => {
    // Context hooks
    const { recipes, foods, details, brands, merchants, locations } = useAssets();
    const { plan, profile } = useMealPlanData();
    const { getMealsByOffsetAndType } = useMealOperations();
    const { canWrite, isPro, canViewNutrition } = usePermissions();
    const { isMobile, isCordova } = useUIState();
    const { editMealBatches } = useModalControls();

    // Local state
    const [dishIndex, setDishIndex] = useState(0);

    // ScreenService integration - keep device awake while viewing meal details
    useEffect(() => {
        ScreenService.keepAwake();

        return () => {
            ScreenService.allowSleep();
        };
    }, []);

    // Get live meal data from context
    const meals = useMemo(() => {
        return getMealsByOffsetAndType(offset, mealType.name) || [];
    }, [getMealsByOffsetAndType, offset, mealType.name]);

    // Memoized calculations
    const sortedMeals = useMemo(() => {
        if (!Array.isArray(meals)) {
            return [];
        }

        // Enrich meals with sorting flags, then sort
        const enrichedMeals = enrichMealsForSorting([...meals], recipes, details, foods);
        return enrichedMeals.sort(mealSortCompare);
    }, [meals, recipes, details, foods]);

    const primary = useMemo(() => {
        if (!sortedMeals.length) return null;
        // Pass true for alreadySorted since sortedMeals are already sorted
        return getPrimaryMeal(sortedMeals, recipes, foods, false, true);
    }, [sortedMeals, recipes, foods]);

    const currentMeal = useMemo(() => {
        return sortedMeals[dishIndex] || sortedMeals[0];
    }, [sortedMeals, dishIndex]);

    // Calculate participants for HowMuchToEatSection
    const participants = useMemo(() => {
        if (!primary?.primary?.participants || !profile?.family) {
            return [{ uuid: 'USER', name: profile.first_name || profile.name, portion: profile.portion }];
        }

        const participantIds = primary.primary.participants;

        const result = participantIds.map(id => {
            if (id === 'USER' || id === profile.uuid) {
                return { uuid: 'USER', name: profile.first_name || profile.name, portion: profile.portion };
            }
            
            const familyMember = profile.family.find(member => member.uuid === id);
            if (familyMember) {
                return familyMember;
            }
            
            // Check if it's a guest (empty string UUID)
            if (id === "") {
                return { uuid: "", name: "Guest", portion: 1 };
            }
            
            // Fallback for other participants
            return { uuid: id, name: `Participant ${id.substring(0, 8)}`, portion: 1 };
        });


        return result;
    }, [primary, profile]);

    // Event handlers

    const handleScrollToDishIndex = (index) => {
        if (!sortedMeals[index]) {
            return;
        }
        setDishIndex(index);
    };

    const handleEditMealServings = () => {
        // Open the EditMealServings modal with the primary meal
        editMealBatches(primary.primary);
    };

    const handleChangePortionAmount = (meal, amount) => {
        // Handle portion amount changes for HowMuchToEat component
        // This would integrate with meal operations for updating portions
        console.log('Change portion amount:', meal, amount);
    };

    // Reset dishIndex if current dish doesn't exist (e.g., when meals change)
    useEffect(() => {
        if (!sortedMeals[dishIndex]) {
            setDishIndex(0);
        }
    }, [sortedMeals, dishIndex]);


    // Rendering functions

    // Content rendering functions
    const renderMealContent = () => {
        if (!currentMeal) return null;

        // Get the primary meal data specifically for the current dish
        const currentPrimary = getPrimaryMeal([currentMeal], recipes, foods);
        if (!currentPrimary) return null;

        const { content } = currentPrimary;
        if (!content) return null;

        if (content.type === 'recipe') {
            return (
                <RecipeContentDisplay
                    meal={currentMeal}
                    recipe={content}
                    details={details}
                    profile={profile}
                    sortedMeals={sortedMeals}
                    dishIndex={dishIndex}
                />
            );
        }
        if (content.type === 'food') {
            return (
                <FoodContentDisplay
                    meal={currentMeal}
                    food={content}
                    profile={profile}
                    sortedMeals={sortedMeals}
                    dishIndex={dishIndex}
                />
            );
        }

        return null;
    };




    // Early return if no data
    if (!Array.isArray(meals) || meals.length === 0) {
        return null;
    }

    if (!primary) {
        return null;
    }

    // Modal classes
    const modalClasses = classNames('el-modal', 'el-modal1', 'meal-details-with-hooks', {
        'is-mobile': isMobile,
        'is-cordova': isCordova,
        'safari-mobile': platform.name === 'Safari' && isMobile,
    });

    const containerClasses = classNames('el-modal-container', 'el-modal1-container', 'meal-details-container', {
        'control-bar-mobile': isMobile,
    });
    return (
        <Modal
            isOpen={true}
            onRequestClose={closeModal}
            className={modalClasses}
            contentLabel="Meal Details"
            overlayClassName="el-modal-overlay"
            closeTimeoutMS={250}
        >
            <section className={containerClasses}>
                {/* Header using el-modal1 pattern */}
                <header data-has-controls="false">
                    <button
                        onClick={closeModal}
                        className="el-modal-back-btn"
                        title="Close meal details"
                    >
                        <span>Back</span>
                    </button>

                    {/* Mobile: Show meal info in header */}
                    {isMobile ? (
                        <div className="details-header">
                            <h2>DAY {offset + 1}, {mealType.name.toUpperCase()}</h2>

                            {/* Mobile meal servings button */}
                            <EditMealServingsButton
                                primary={primary}
                                sortedMeals={sortedMeals}
                                profile={profile}
                                plan={plan}
                                recipes={recipes}
                                onEditMealServings={handleEditMealServings}
                            />

                            {/* Mobile control bar */}
                            <div className="el-modal-controls el-modal1-controls">
                                <ControlBar
                                    meals={sortedMeals}
                                    offset={offset}
                                    mealType={mealType}
                                    primary={primary}
                                    dishIndex={dishIndex}
                                    sortedMeals={sortedMeals}
                                    hidePrintBtn={hidePrintBtn}
                                />
                            </div>
                        </div>
                    ) : (
                        <h2>Meal Information</h2>
                    )}
                </header>

                {/* Main content area */}
                <div className="el-modal-body-container el-modal1-body-container el-fonts">
                    {/* Desktop: Show details-header in body */}
                    {!isMobile ? (
                        <div className="details-header">
                            <h6>
                                DAY {offset + 1}, <em>{mealType.name.toUpperCase()}</em>
                            </h6>

                            {/* Desktop meal servings button */}
                            <EditMealServingsButton
                                primary={primary}
                                sortedMeals={sortedMeals}
                                profile={profile}
                                plan={plan}
                                recipes={recipes}
                                onEditMealServings={handleEditMealServings}
                            />

                            {/* Desktop control bar */}
                            <ControlBar
                                meals={sortedMeals}
                                offset={offset}
                                mealType={mealType}
                                primary={primary}
                                dishIndex={dishIndex}
                                sortedMeals={sortedMeals}
                                hidePrintBtn={hidePrintBtn}
                            />
                        </div>
                    ) : null}

                    {/* SpringHero for dish navigation - wrapper for modal-specific styling */}
                    <div className="spring-hero-wrapper">
                        <SpringHero
                            meals={sortedMeals}
                            recipes={recipes}
                            foods={foods}
                            details={details}
                            merchants={merchants}
                            locations={locations}
                            brands={brands}
                            profile={profile}
                            dishIndex={dishIndex}
                            scrollToDishIndex={handleScrollToDishIndex}
                            isMobile={isMobile}
                        />
                    </div>

                    {/* Meal Content (Recipe or Food) */}
                    {renderMealContent()}

                    <HowMuchToEatSection
                        meals={sortedMeals}
                        participants={participants}
                        profile={profile}
                        recipes={recipes}
                        sectionClass={sections[2]}
                        onChangePortionAmount={handleChangePortionAmount}
                    />

                    {canViewNutrition && (
                        <div className={classNames('details-section', sections[3])}>
                            <header>
                                <div className="section-title">
                                    <p className="t2">Nutrition Information</p>
                                </div>
                            </header>
                            <div className="section-body">
                                <MealNutritionTabs
                                    sortedMeals={sortedMeals}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>

        </Modal>
    );
};

MealDetails.propTypes = {
    offset: PropTypes.number.isRequired,
    mealType: PropTypes.shape({ // Should be meal type object with timing, participants, etc.
        name: PropTypes.string.isRequired,
        begin: PropTypes.number,
        end: PropTypes.number,
        timing: PropTypes.object,
        participants: PropTypes.array,
        envelope: PropTypes.object,
    }).isRequired,
    closeModal: PropTypes.func.isRequired,
    hidePrintBtn: PropTypes.bool,
};

export default MealDetails;
